// /v1/custom-fields API client + the fieldType casing normalizer.
//
// CASING GROUND TRUTH (plan open question #1, verified against the live
// OpenAPI spec and live reads on 2026-07-29):
//   · POST body enums are camelCase — fieldType: "text" | "url" | "date" |
//     "dropDownList" | "readOnly" | "number" | "checkbox" | "time" |
//     "attachments" | "currency" | "user"; fieldset: "workRequestDetails" etc.
//   · The READ model returns fieldType as an INTEGER enum plus a display-label
//     string in fieldTypeName ("Drop down list", "Read only"). The int enum
//     has a gap at 5 (retired type): text=1, url=2, date=3, dropDownList=4,
//     readOnly=6, number=7, checkbox=8, time=9, attachments=10, currency=11,
//     user=12.
//   · post-options option-map KEYS are PascalCase ("DropDownList",
//     "WorkRequestDetails") with display labels as values.
// normalizeFieldType() accepts all four shapes and emits the canonical
// camelCase POST value.
//
// This module stays node-testable: it imports only apiClient (plain fetch
// wrappers — nothing runs at import time) and never Supabase. The paginated
// sweep is implemented locally instead of reusing fmxSync's fetchAllPages,
// because importing fmxSync would drag db.js → supabase.js into the module
// graph and break plain-Node test scripts.

import { fmxFetch, fmxErrorMessage } from '../apiClient.js';

// One row per FMX custom-field type. `post` is the canonical POST enum value;
// `int` + `label` are what the read model returns; `pascal` is what
// post-options option maps key on.
export const FIELD_TYPES = [
  { post: 'text',         pascal: 'Text',         int: 1,  label: 'Text' },
  { post: 'url',          pascal: 'URL',          int: 2,  label: 'URL' },
  { post: 'date',         pascal: 'Date',         int: 3,  label: 'Date' },
  { post: 'dropDownList', pascal: 'DropDownList', int: 4,  label: 'Drop down list' },
  { post: 'readOnly',     pascal: 'ReadOnly',     int: 6,  label: 'Read only' },
  { post: 'number',       pascal: 'Number',       int: 7,  label: 'Number' },
  { post: 'checkbox',     pascal: 'Checkbox',     int: 8,  label: 'Checkbox' },
  { post: 'time',         pascal: 'Time',         int: 9,  label: 'Time' },
  { post: 'attachments',  pascal: 'Attachments',  int: 10, label: 'Attachments' },
  { post: 'currency',     pascal: 'Currency',     int: 11, label: 'Currency' },
  { post: 'user',         pascal: 'User',         int: 12, label: 'User' },
];

export const FIELD_TYPE_POST_VALUES = FIELD_TYPES.map(t => t.post);

// Lookup maps built once. String keys are lowercased with spaces/hyphens
// stripped so "Drop down list", "DropDownList", "dropdownlist", and
// "drop-down-list" all resolve to the same row.
const byLoose = new Map();
const byInt = new Map();
for (const t of FIELD_TYPES) {
  byLoose.set(t.post.toLowerCase(), t);
  byLoose.set(t.pascal.toLowerCase(), t);
  byLoose.set(t.label.toLowerCase().replace(/[\s-]+/g, ''), t);
  byInt.set(t.int, t);
}

// Normalize any inbound fieldType shape (camelCase, PascalCase, display
// label, or read-model integer) to the canonical camelCase POST value.
// Returns null for anything unrecognized — callers must treat null as a
// validation error, never guess.
export function normalizeFieldType(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return byInt.get(value)?.post || null;
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, '');
  return byLoose.get(key)?.post || null;
}

export function fieldTypeLabel(value) {
  const post = normalizeFieldType(value);
  return FIELD_TYPES.find(t => t.post === post)?.label || String(value ?? '');
}

// ── Live reads (all through the /api/fmx proxy — GETs only in Milestone 1) ──

// GET /v1/custom-fields/post-options?fieldset=X. The fieldset param is
// required by the API (omitting it is a 400). The response's systemFields
// are the authority on which keys the fieldset accepts, which of them are
// required, and the live ID→name maps for scoping values (modules, request
// types, user type restrictions, …).
export async function fetchCustomFieldsPostOptions(projectId, fieldset) {
  const res = await fmxFetch({
    projectId,
    endpoint: `/v1/custom-fields/post-options?fieldset=${encodeURIComponent(fieldset)}`,
    method: 'GET',
  });
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch { /* non-JSON error body */ }
    throw new Error(`post-options failed for ${fieldset} (${res.status}): ${fmxErrorMessage(body, res.status)}`);
  }
  const data = await res.json();
  return { fieldset, systemFields: data.systemFields || [] };
}

// Paginated sweep of existing custom fields, optionally filtered by fieldset
// and the server-side `search` param. Used by the duplicate screen. Fields
// kept deliberately small; fieldTypeName is requested because the bare
// fieldType comes back as the read-model integer.
const LIST_FIELDS = 'id,name,fieldset,fieldTypeName,isRequired,isDeleted,options,allowOtherOption,allowMultipleSelections,workRequestModuleID,workTaskModuleID,requestTypeIDs,equipmentTypeIDs,resourceTypeIDs,userTypeIDs,sortPosition';

export async function listCustomFields(projectId, { fieldset, search } = {}) {
  const params = [`fields=${encodeURIComponent(LIST_FIELDS)}`];
  if (fieldset) params.push(`fieldset=${encodeURIComponent(fieldset)}`);
  if (search) params.push(`search=${encodeURIComponent(search)}`);
  const limit = 100;

  const items = [];
  let offset = 0;
  while (true) {
    const endpoint = `/v1/custom-fields?${params.join('&')}&offset=${offset}&limit=${limit}`;
    const res = await fmxFetch({ projectId, endpoint, method: 'GET' });
    if (!res.ok) throw new Error(`FMX returned ${res.status} listing custom fields`);
    const data = await res.json();
    const page = Array.isArray(data) ? data : (data.items || []);
    items.push(...page);
    if (page.length < limit) break;
    offset += limit;
  }
  return items;
}
